<template>
    <v-dialog
        :value="showDialog"
        persistent
        max-width="400px"
        overlay-color="#909090"
        overlay-opacity="1"
    >
        <v-card>
            <div class="text-right" v-if="!showExitConfirmation">
                <v-btn icon color="primary" @click="showExitConfirmation = true">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <v-card-text
                v-if="step === 1 && !showExitConfirmation"
                class="pa-3 px-6"
            >
                <div class="text-center" v-if="loadingQuote">
                    <v-img
                        src="/images/payment-logos/worldremit.png"
                        contain
                        class="align-self-start mr-auto"
                        height="50"
                    />
                    <div class="py-12 my-8">
                        <div class="text-center">
                            <v-progress-circular
                                indeterminate
                                color="primary"
                                class="ml-2"
                            />
                        </div>
                        <h5 class="title primary--text text--primary mt-5">
                            Filling transaction details
                        </h5>
                    </div>
                </div>
            </v-card-text>
            <confirm-payment
                v-if="step === 2 && !showExitConfirmation"
                :value="estimatedQuote"
                @cancel="decline"
                @confirm="step = 3"
            />
            <sms-verification
                v-if="step === 3 && !showExitConfirmation"
                @success="pay"
            />
            <v-card-text class="pa-6" v-if="step === 4 && !showExitConfirmation">
                <div class="text-center py-12">
                    <v-img
                        src="/images/payment-logos/worldremit.png"
                        contain
                        class="align-self-start mr-auto"
                        height="65"
                    />

                    <div>
                        <h5 class="title primary--text text--primary mt-5">
                            You are now being redirected to make payment via WorldRemit
                        </h5>

                        <v-btn
                            class="text-none py-5 subtitle-1 mt-3"
                            dark
                            color="primary"
                            @click="redirect"
                        >
                            Ok
                        </v-btn>
                    </div>
                </div>
            </v-card-text>
            <exit-confirmation
                v-if="showExitConfirmation"
                @continue="showExitConfirmation = false"
                @exit="decline"
            />
        </v-card>
        <loading-overlay :loading="loading" />
    </v-dialog>
</template>

<script>
import ExitConfirmation from './exit-confirmation.vue';
import ConfirmPayment from './confirm-payment.vue';
import SmsVerification from './sms-verification.vue';
import TransactionService from '../../../services/TransactionService';

const wait = (s) => new Promise((r) => setTimeout(r, s * 1000))

export default {
    components: {
        ExitConfirmation,
        ConfirmPayment,
        SmsVerification,
    },
    data() {
        return {
            showDialog: false,
            loading: false,
            step: 1,
            showExitConfirmation: false,
            loadingQuote: false,
            estimatedQuote: null,
            paymentUrl: null,
            match: null,
        };
    },
    methods: {
        async show(match) {
            this.showDialog = true;
            this.loading = false;
            this.step = 1;
            this.showExitConfirmation = false;
            this.loadingQuote = null;
            this.estimatedQuote = null;
            this.paymentUrl = null;
            this.match = match,

            await this.getEstimatedQuote();
        },
        async getEstimatedQuote() {
            try {
                this.loadingQuote = true;
                const { data } = await Vue.backendApi().pendingMatches(this.match.id)
                    .worldremit()
                    .getQuote();

                this.estimatedQuote = data;
                this.step = 2;
                this.loadingQuote = false;
                return data;
            } catch (err) {
                this.loadingQuote = false;
                this.$swal({
                    type: 'error',
                    title: 'Unable to get estimated quote',
                    text: err
                });
                throw err;
            }
        },
        async pay() {
            try {
                this.loading = true;
                const { data } = await Vue.backendApi().pendingMatches(this.match.id)
                    .worldremit()
                    .pay();

                this.paymentUrl = data.paymentUrl;
                this.step = 4;
                this.loading = false;
            } catch (err) {
                this.loading = false;
                this.$swal({
                    type: 'error',
                    title: 'Something went wrong',
                    text: err
                });
            }
        },
        redirect() {
            const windowMessageListener = (event) => {
                if (event.data.type === 'AUTO_FILLER_WORLDREMIT_PAYMENT_STATUS') {
                    this.paymentCallback(event.data);
                    window.removeEventListener('message', windowMessageListener);
                }
            };

            window.removeEventListener('message', windowMessageListener);
            window.addEventListener('message', windowMessageListener);

            window.open(this.paymentUrl, '_blank');
        },
        async paymentCallback({ status }) {
            if (status === 'failure') {
                this.$swal({
                    type: 'error',
                    title: 'Card Processing Failed',
                    text: `Your debit card failed to process.
                        Please make sure you entered the correct details and
                        you have sufficient funds in your bank account.
                        If error still persists, then you may need to contact
                        your bank or financial institution to rectify this issue.
                        Thank you`,
                    showCancelButton: true,
                    showConfirmButton: true,
                    confirmButtonText: 'Retry',
                }).then(({ value }) => {
                    if (value) {
                        this.step = 2;
                        this.pay();
                    } else {
                        this.decline();
                    }
                });
            }

            if (status === 'cancel') {
                this.$swal({
                    type: 'error',
                    title: 'Your transfer has been cancelled',
                });
                this.decline();
            }

            if (status === 'success') {
                this.$swal({
                    type: 'success',
                    title: 'Your transfer has been successfully initiated',
                });
                
                try {
                    this.loading = true;
                    await wait(20);
                    await TransactionService.validate(this, this.match, {
                        is_coupon_used: true,
                    });
                } catch (err) {
                } finally {
                    this.loading = false;
                    this.finish();
                }
            }
        },
        decline() {
            this.showDialog = false;
            this.loading = false;
            this.showExitConfirmation = false;
            this.loadingQuote = null;
            this.estimatedQuote = null;
            this.match = null;
        },
        finish() {
            this.showDialog = false;
            this.loading = false;
            this.showExitConfirmation = false;
            this.loadingQuote = null;
            this.estimatedQuote = null;
            this.match = null;

            this.$emit('success');
        },
    },
};
</script>
